import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, ShieldAlert, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Forced password rotation modal. Shown right after login when the user
 * has `must_change_password: true` (typically a freshly admin-provisioned
 * account using a temporary password). The modal is non-dismissable —
 * it overlays the dashboard until the rotation succeeds.
 *
 * On success, refreshes the auth context so the flag clears.
 */
export default function ForcePasswordChangeModal() {
  const { user, refresh } = useAuth();
  const open = !!user?.must_change_password;
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const mismatch = newPassword && confirm && newPassword !== confirm;
  const sameAsOld = newPassword && oldPassword && newPassword === oldPassword;
  const canSubmit = oldPassword && newPassword && confirm && !mismatch && !sameAsOld && newPassword.length >= 8;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirm,
      });
      toast.success("Mot de passe modifié avec succès");
      setOldPassword(""); setNewPassword(""); setConfirm("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={() => { /* non-dismissable */ }}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="force-password-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" />
            Changement de mot de passe obligatoire
          </DialogTitle>
          <DialogDescription>
            Votre compte a été créé avec un mot de passe temporaire. Pour des
            raisons de sécurité, vous devez le remplacer avant de continuer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4" autoComplete="off">
          <div>
            <Label>Mot de passe actuel (temporaire)</Label>
            <div className="relative mt-1">
              <Input
                type={showOld ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Celui reçu par email"
                required
                autoFocus
                data-testid="force-pw-old"
              />
              <button type="button" onClick={() => setShowOld((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showOld ? "Masquer" : "Afficher"}>
                {showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label>Nouveau mot de passe</Label>
            <div className="relative mt-1">
              <Input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 8 caractères — 1 maj, 1 chiffre, 1 spécial"
                required
                minLength={8}
                data-testid="force-pw-new"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showNew ? "Masquer" : "Afficher"}>
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {sameAsOld && (
              <p className="mt-1 text-xs text-destructive" data-testid="force-pw-same-error">
                Le nouveau mot de passe doit être différent de l&apos;ancien.
              </p>
            )}
          </div>

          <div>
            <Label>Confirmer le nouveau mot de passe</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Retapez le nouveau mot de passe"
              required
              minLength={8}
              className="mt-1"
              data-testid="force-pw-confirm"
            />
            {mismatch && (
              <p className="mt-1 text-xs text-destructive" data-testid="force-pw-mismatch">
                Les deux mots de passe ne correspondent pas.
              </p>
            )}
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={!canSubmit || busy}
              className="w-full"
              data-testid="force-pw-submit"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Modifier mon mot de passe
            </Button>
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              Un email de confirmation vous sera envoyé après la modification.
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
